import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import KpiTile from "@/components/KpiTile";
import StatusBadge from "@/components/StatusBadge";
import { usd, shortDate, daysFromNow } from "@/lib/format";
import {
  Ship, Package, AlertTriangle, Clock, Receipt, DollarSign, TrendingUp, ListChecks, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [shipments, setShipments] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/summary").then(r => setSummary(r.data));
    api.get("/dashboard/alerts").then(r => setAlerts(r.data));
    api.get("/shipments").then(r => setShipments(r.data));
  }, []);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Control Tower</h1>
          <p className="text-sm text-zinc-500 mt-1">Real-time freight cost &amp; risk view — before, during and after shipment.</p>
        </div>
        <div className="text-right text-xs text-zinc-500">
          <div className="data-label mb-1">Last updated</div>
          <div className="metric">{new Date().toLocaleTimeString("en-AU")}</div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200" data-testid="kpi-grid">
        <KpiTile testId="kpi-open-bookings" label="Open Bookings" value={summary?.open_bookings ?? "—"} sublabel="in pipeline" icon={ListChecks} accent="primary" />
        <KpiTile testId="kpi-in-transit" label="In Transit" value={summary?.in_transit ?? "—"} sublabel="containers afloat" icon={Ship} />
        <KpiTile testId="kpi-at-risk" label="At-Risk Shipments" value={summary?.at_risk ?? "—"} sublabel="needs action today" icon={AlertTriangle} accent="critical" trend={12} />
        <KpiTile testId="kpi-free-time" label="Free-Time Expiring" value={summary?.free_time_expiring ?? "—"} sublabel="≤ 3 days remaining" icon={Clock} accent="warning" />

        <KpiTile testId="kpi-detention" label="Detention Exposure" value={usd(summary?.detention_exposure_usd)} sublabel="open D&D liability" icon={AlertTriangle} accent="critical" />
        <KpiTile testId="kpi-invoice-exceptions" label="Invoice Exceptions" value={summary?.invoice_exceptions ?? "—"} sublabel="needs audit" icon={Receipt} accent="warning" />
        <KpiTile testId="kpi-monthly-spend" label="Monthly Freight Spend" value={usd(summary?.monthly_spend_usd)} sublabel="MTD" icon={DollarSign} trend={-4} />
        <KpiTile testId="kpi-savings" label="Savings Identified" value={usd(summary?.savings_identified_usd)} sublabel="recoverable via audit" icon={TrendingUp} accent="success" trend={18} />
      </div>

      {/* Workflow strip: before / during / after */}
      <div className="grid grid-cols-3 gap-px bg-zinc-200 border border-zinc-200" data-testid="workflow-strip">
        {[
          { phase: "BEFORE", title: "Control Procurement", body: "Benchmark rates, calculate surcharges, plan bookings.", cta: "Open Procurement", path: "/procurement" },
          { phase: "DURING", title: "Control Visibility", body: "Track shipments, predict ETA, manage exceptions and documents.", cta: "Open Visibility", path: "/shipments" },
          { phase: "AFTER", title: "Control Spend", body: "Audit invoices, dispute detention, score carriers, capture savings.", cta: "Open Audit", path: "/invoices" },
        ].map((p) => (
          <div key={p.phase} className="bg-white p-6">
            <div className="data-label text-[#002FA7] mb-3">{p.phase} SHIPMENT</div>
            <h3 className="font-heading text-xl font-bold mb-2">{p.title}</h3>
            <p className="text-sm text-zinc-600 leading-relaxed mb-4">{p.body}</p>
            <Button
              data-testid={`workflow-cta-${p.phase.toLowerCase()}`}
              variant="ghost"
              className="rounded-none p-0 h-auto text-[#002FA7] hover:text-[#002480] hover:bg-transparent"
              onClick={() => navigate(p.path)}
            >
              {p.cta} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>
        ))}
      </div>

      {/* Alerts + Shipments */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
            <h3 className="font-heading text-lg font-bold tracking-tight">Action Center</h3>
            <span className="metric text-[11px] text-zinc-500">{alerts.length} alerts</span>
          </div>
          <div className="max-h-[480px] overflow-auto" data-testid="alerts-list">
            {alerts.map(a => (
              <div key={a.id} className="px-5 py-3 border-b border-zinc-100 hover:bg-zinc-50/60 cursor-pointer">
                <div className="flex items-center justify-between mb-1">
                  <StatusBadge value={a.severity} testId={`alert-sev-${a.id}`} />
                  <span className="metric text-[10px] text-zinc-400">{a.type}</span>
                </div>
                <div className="text-sm text-zinc-900 leading-snug">{a.message}</div>
                <div className="text-[11px] text-zinc-500 mt-1 metric">{a.shipment_id} · {a.lane}</div>
              </div>
            ))}
            {alerts.length === 0 && <div className="p-10 text-center text-sm text-zinc-500">No active alerts</div>}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
            <h3 className="font-heading text-lg font-bold tracking-tight">Active Shipments</h3>
            <Button
              data-testid="view-all-shipments"
              variant="ghost" size="sm"
              className="rounded-none text-[#002FA7] hover:bg-[#E6EBF6]"
              onClick={() => navigate('/shipments')}
            >View all <ArrowRight className="w-3 h-3 ml-1" /></Button>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">
                  {["Shipment", "Lane", "Carrier", "ETA", "Status", "LFD"].map(h => (
                    <th key={h} className="px-4 py-2.5 data-label">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shipments.slice(0, 10).map(s => {
                  const lfdDays = daysFromNow(s.last_free_day);
                  return (
                    <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50/60 cursor-pointer" onClick={() => navigate(`/shipments?focus=${s.id}`)}>
                      <td className="px-4 py-2.5 metric font-medium">{s.id}</td>
                      <td className="px-4 py-2.5 text-zinc-700">{s.origin_name} <span className="text-zinc-400">→</span> {s.destination_name}</td>
                      <td className="px-4 py-2.5 text-zinc-700">{s.carrier}</td>
                      <td className="px-4 py-2.5 metric text-zinc-700">{shortDate(s.eta_actual)} {s.delay_days > 0 && <span className="text-[#B45309]">+{s.delay_days}d</span>}</td>
                      <td className="px-4 py-2.5"><StatusBadge value={s.status} /></td>
                      <td className="px-4 py-2.5 metric text-xs">{lfdDays !== null && (lfdDays < 0 ? <span className="text-[#991B1B]">{lfdDays}d</span> : <span className={lfdDays <= 2 ? "text-[#B45309]" : "text-zinc-500"}>{lfdDays}d</span>)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
